/**
 * Seller-facing data request list. The pre-offer half of the buyer-side diligence
 * checklist is the letter a buyer sends the seller or broker, so outstanding
 * pre-offer rows become that letter: grouped by category, numbered, plain language,
 * no internal ids, no evidence columns, no scores. Partial rows say what is still
 * outstanding.
 */
import { docxParts } from '../engine/capitalDocx.js';
import { downloadBytes } from '../engine/ooxmlZip.js';
import { CATEGORY_LABEL } from './checklist.js';

const { p, h1, h2, table, buildDocx, DOCX_MIME } = docxParts;
const NAVY = '0C1F2E';
const MUTED = '64748B';

export function requestRows(result) {
  return (result?.items || []).filter(
    (i) => i.phase === 'pre_offer' && (i.status === 'Missing' || i.status === 'Partial'),
  );
}

function longDate(iso) {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso || '';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
}

function askFor(item) {
  if (item.status === 'Partial' && item.source === 'auto' && item.detail) {
    return `${item.whatSatisfies} Still outstanding: ${item.detail}`;
  }
  return item.whatSatisfies || item.item;
}

function banner(title, subtitle) {
  return `
  <w:p>
    <w:pPr><w:shd w:val="clear" w:color="auto" w:fill="${NAVY}"/><w:spacing w:before="0" w:after="0"/><w:jc w:val="center"/></w:pPr>
    <w:r><w:rPr><w:rFonts w:ascii="Calibri" w:hAnsi="Calibri"/><w:b/><w:sz w:val="28"/><w:color w:val="FFFFFF"/></w:rPr>
      <w:t xml:space="preserve">${title.replace(/&/g, '&amp;').replace(/</g, '&lt;')}</w:t></w:r>
  </w:p>
  <w:p>
    <w:pPr><w:shd w:val="clear" w:color="auto" w:fill="E8A317"/><w:spacing w:before="0" w:after="200"/><w:jc w:val="center"/></w:pPr>
    <w:r><w:rPr><w:rFonts w:ascii="Calibri" w:hAnsi="Calibri"/><w:b/><w:sz w:val="18"/><w:color w:val="${NAVY}"/></w:rPr>
      <w:t xml:space="preserve">${subtitle.replace(/&/g, '&amp;').replace(/</g, '&lt;')}</w:t></w:r>
  </w:p>`;
}

/**
 * @param {object} result computeChecklist output
 * @param {{ dealLabel?: string, seller?: string, buyer?: string, asOf?: string, replyBy?: string }} opts
 */
export function buildRequestListDocx(result, opts = {}) {
  const dealLabel = opts.dealLabel || 'Deal';
  const seller = opts.seller || 'Seller';
  const buyer = opts.buyer || result?.deal?.buyer?.name || 'Buyer';
  const asOf = opts.asOf || result?.dates?.today || new Date().toISOString().slice(0, 10);
  const rows = requestRows(result);
  const parts = [
    banner(`${buyer} · ${dealLabel}`, 'Data request list'),
    h1('Data request list'),
    p(`To: ${seller}`, { size: 20, spaceAfter: 40 }),
    p(`From: ${buyer}`, { size: 20, spaceAfter: 40 }),
    p(`Date: ${longDate(asOf)}${opts.replyBy ? ` · Requested by ${longDate(opts.replyBy)}` : ''}`, { size: 20, spaceAfter: 160 }),
    p(
      `Thank you for the data room access. To complete our pre-offer review of ${dealLabel} we need the ${rows.length} item${rows.length === 1 ? '' : 's'} below. Where an item is marked partial we have part of it and note what is still outstanding. Please upload to the data room in the folder that fits, or send to your contact at ${buyer}.`,
      { size: 20, spaceAfter: 160 },
    ),
  ];
  let n = 0;
  for (const cat of Object.keys(CATEGORY_LABEL)) {
    const inCat = rows.filter((r) => r.category === cat);
    if (!inCat.length) continue;
    parts.push(h2(CATEGORY_LABEL[cat]));
    parts.push(
      table(
        ['#', 'Item', 'What we need', 'Status'],
        inCat.map((r) => {
          n += 1;
          return [String(n), r.item, askFor(r), r.status === 'Partial' ? 'Partial' : 'Outstanding'];
        }),
      ),
    );
  }
  if (!rows.length) parts.push(p('Nothing is outstanding for the pre-offer review.', { size: 20 }));
  parts.push(
    p(`Prepared by Data2Dollars.ai for ${buyer} · Confidential under the confidentiality agreement`, {
      size: 14,
      color: MUTED,
      center: true,
      spaceAfter: 0,
    }),
  );
  return buildDocx(parts.join(''));
}

export function downloadRequestListDocx(result, opts = {}) {
  const base = (opts.dealId || opts.dealLabel || 'deal').toString().replace(/[^a-z0-9]+/gi, '-').toLowerCase();
  downloadBytes(`${base}-data-request-list.docx`, buildRequestListDocx(result, opts), DOCX_MIME);
}
